import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import BottomSheetContent from '../../../components/BottomSheetComponents/BottomSheetContent'
import RiveComponent, { RiveAlignment, RiveFit } from '../../../components/RiveComponent'
import RenderHTML from "../../../components/ui-components/RenderHTML"
import { GROUPED_STATIC_TEXT_ROLE } from '../../../components/ui-components/NameValueTableRow'
import GreyLockSVG from "../../../icons/GreyLockSVG"
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import { InfoIcon } from '../icons/InfoIcon'
import { useNewEliteLandingAnalyticsScreen } from '../analytics/NewEliteLandingAnalyticsContext'
import {
    NewEliteLandingBottomSheetName,
    NewEliteLandingEventId,
    trackNewEliteBottomSheetLoaded,
    trackNewEliteUserClicked
} from '../analytics/newEliteLandingAnalytics'
import { RebalanceWrapperSection, type RebalanceWrapperSectionProps } from './RebalanceWrapperSection'
import { useEliteBuyCtaExtras } from '../../../components/elite/EliteBuyCtaExtrasContext'
import type { EliteEventExtras } from '../../../utils/eliteEventExtras'

const SWIPE_THRESHOLD_PX = 50

type BenefitItem = {
    title: string
    isExclusive?: boolean
    eliteValue: 'check' | 'coming-soon'
}

type BenefitSection = {
    title: string
    items: BenefitItem[]
}

const benefitSections: BenefitSection[] = [
    {
        title: 'FUND SELECTION & REVIEW',
        items: [
            { title: 'Power Rank', eliteValue: 'check' },
            { title: 'Power Select', eliteValue: 'check' },
            { title: 'Rank Trends', eliteValue: 'check' }
        ]
    },
    {
        title: 'PORTFOLIO REBALANCING',
        items: [
            { title: 'Power Rebalance', eliteValue: 'check' },
            { title: 'Regular to Direct plan', eliteValue: 'check' }
        ]
    },
    {
        title: 'HIGH QUALITY RESEARCH',
        items: [{ title: 'Elite Lens', eliteValue: 'check' }]
    },
    {
        title: 'TAX',
        items: [
            { title: 'Tax Saving Plan', isExclusive: true, eliteValue: 'check' },
            // { title: 'Power Allocation', isExclusive: true, eliteValue: 'coming-soon' },
            // { title: 'SIP Strategy', isExclusive: true, eliteValue: 'coming-soon' }
        ]
    }
]

export type NewEliteLandingPageBenefitsProps = {
    heading?: string
    eliteOnly?: boolean
    rebalanceSection?: RebalanceWrapperSectionProps
    topContent?: ReactNode
}

export default function NewEliteLandingPageBenefits({
    heading = 'Elite benefits',
    eliteOnly = false,
    rebalanceSection,
    topContent
}: NewEliteLandingPageBenefitsProps) {
    const { openBottomSheet, closeBottomSheet } = useBottomSheet()
    const analyticsScreen = useNewEliteLandingAnalyticsScreen()
    const eliteBuyCtaExtras = useEliteBuyCtaExtras()

    const handleInfoClick = (itemTitle: string) => {
        trackNewEliteUserClicked(analyticsScreen, NewEliteLandingEventId.BenefitInfoOpen, {
            ...eliteBuyCtaExtras,
            buttonLabel: itemTitle,
            bottomSheetName: NewEliteLandingBottomSheetName.BenefitsInfo
        })
        openBottomSheet(
            <BenefitsInfoBottomSheet
                onDismiss={closeBottomSheet}
                initialTabTitle={itemTitle}
                tabTitles={getBenefitTabTitles(benefitSections)}
                buyCtaExtras={eliteBuyCtaExtras}
            />
        )
    }

    return (
        <div className='mt-10'>
            <div className='relative z-[1] bg-white pb-8 shadow-[0_-14px_40px_0_rgba(200,97,54,0.14),0_14px_40px_0_rgba(200,97,54,0.14)]'>
                {topContent}
                {rebalanceSection ? <RebalanceWrapperSection {...rebalanceSection} /> : null}
                <div className='mb-10 flex items-center justify-between px-5 pt-8'>
                    <p className='text-lg font-medium text-grey10'>{heading}</p>
                    <div className='mr-[13px] flex items-center gap-[1.65rem]'>
                        <p className='text-base font-medium text-orange30'>Elite</p>
                        {eliteOnly ? null : <p className='text-base font-medium text-[#3D3D3D]'>Free</p>}
                    </div>
                </div>
                <div className='space-y-10 px-5'>
                    {benefitSections.map((section) => (
                        <BenefitSectionRow
                            key={section.title}
                            section={section}
                            onInfoClick={handleInfoClick}
                            eliteOnly={eliteOnly}
                        />
                    ))}
                </div>
            </div>
        </div>
    )
}

export function BenefitSectionRow({
    section,
    onInfoClick,
    eliteOnly = false
}: {
    section: BenefitSection
    onInfoClick: (itemTitle: string) => void
    eliteOnly?: boolean
}) {
    return (
        <div className='space-y-6'>
            <div className='flex items-center gap-3'>
                <p className='text-[11px] font-medium uppercase tracking-[1px] text-grey40 opacity-60'>{section.title}</p>
                <div className='h-px flex-1 bg-[#E4E6E8]' />
            </div>
            <div className='space-y-5'>
                {section.items.map((item) => (
                    <BenefitItemRow key={item.title} item={item} onInfoClick={onInfoClick} eliteOnly={eliteOnly} />
                ))}
            </div>
        </div>
    )
}

const TickSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M5.39431 14C5.067 14.0006 4.74361 13.9248 4.44723 13.7782C4.15084 13.6316 3.88878 13.4177 3.67975 13.1518L1.72248 10.5248C1.63997 10.4192 1.57828 10.2973 1.54103 10.1661C1.50378 10.035 1.49172 9.89731 1.50556 9.76119C1.5194 9.62507 1.55886 9.49323 1.62163 9.37343C1.68439 9.25363 1.76919 9.14828 1.87105 9.06356C1.97292 8.97884 2.08978 8.91646 2.21479 8.88008C2.3398 8.8437 2.47044 8.83406 2.59903 8.85173C2.72762 8.86939 2.85157 8.914 2.9636 8.98293C3.07563 9.05187 3.17349 9.14374 3.25143 9.25316L5.19832 11.8568C5.22136 11.8901 5.25282 11.9158 5.28909 11.9311C5.32536 11.9464 5.36498 11.9505 5.40339 11.9431C5.44581 11.9431 5.48769 11.9331 5.52589 11.9136C5.5641 11.8941 5.59765 11.8658 5.62404 11.8308L12.7587 2.39601C12.8379 2.28874 12.9365 2.19913 13.0487 2.13236C13.1609 2.0656 13.2846 2.02302 13.4125 2.00708C13.5404 1.99114 13.6701 2.00217 13.7939 2.03952C13.9178 2.07688 14.0334 2.13981 14.134 2.22468C14.2347 2.30955 14.3184 2.41466 14.3803 2.53393C14.4422 2.6532 14.481 2.78425 14.4946 2.9195C14.5082 3.05474 14.4962 3.19148 14.4593 3.32179C14.4225 3.4521 14.3615 3.5734 14.2799 3.67864L7.13223 13.123C6.92449 13.3953 6.66189 13.6153 6.3635 13.767C6.06512 13.9187 5.73846 13.9983 5.40728 14H5.39431Z" fill="#CC5C2D"/>
</svg>`

export function BenefitItemRow({
    item,
    onInfoClick,
    eliteOnly = false
}: {
    item: BenefitItem
    onInfoClick: (itemTitle: string) => void
    eliteOnly?: boolean
}) {
    return (
        <div className='flex items-center justify-between'>
            <div className='flex flex-col'>
                <div className='flex items-center gap-1'>
                    <p className='text-[14px] font-medium leading-[1.3] text-[#232323]'>{item.title}</p>
                    <div onClick={() => onInfoClick(item.title)} className='cursor-pointer'>
                        <RenderHTML innerHTMLText={InfoIcon} />
                    </div>
                </div>
                {item.isExclusive ? (
                    <p className='mt-1 border-l-[2px] border-[#D1D6D6] bg-[linear-gradient(90deg,_#F3F3F3_0%,_rgba(243,243,243,0)_100%)] pl-[3px] text-[10px] uppercase tracking-[0.5px] text-grey40'>EXCLUSIVE TO YEARLY PLAN</p>
                ) : null}
            </div>
            <div
                className={`flex items-center ${eliteOnly ? '' : item.eliteValue === 'check' ? 'gap-12' : 'gap-6'}`}
            >
                {item.eliteValue === 'check' ? (
                    <p className={`text-[24px] leading-none text-[#CC5C2D] ${eliteOnly ? 'mr-5' : ''}`}>
                        <RenderHTML innerHTMLText={TickSVG} />
                    </p>
                ) : (
                    <p className='text-center text-[11px] uppercase leading-[1.2] tracking-[1px] text-[#CC5C2D] mr-[6px]'>
                        Coming
                        <br />
                        Soon
                    </p>
                )}
                {eliteOnly ? null : (
                    <p className='text-xl leading-none text-[#A0A5A5] mr-[19px]'>
                        <span>
                            <GreyLockSVG />
                        </span>
                    </p>
                )}
            </div>
        </div>
    )
}

export function BenefitsInfoBottomSheet({
    onDismiss,
    initialTabTitle,
    tabTitles,
    buyCtaExtras = {},
    sheetTitle = 'Unlock with Elite',
    benefitInfoMap = benefitInfoContentMap
}: {
    onDismiss: () => void
    initialTabTitle: string
    tabTitles: string[]
    // Passed by the in-page opener; this sheet renders in a portal and cannot read context.
    buyCtaExtras?: EliteEventExtras
    sheetTitle?: string
    benefitInfoMap?: Record<string, BenefitInfoContent>
}) {
    const analyticsScreen = useNewEliteLandingAnalyticsScreen()
    const [activeTabTitle, setActiveTabTitle] = useState(initialTabTitle)
    const tabsStripRef = useRef<HTMLDivElement | null>(null)
    const tabStripHasScrolledOnceRef = useRef(false)
    const swipeStartRef = useRef<{ x: number; y: number } | null>(null)
    const [slideDirection, setSlideDirection] = useState(0)
    const prefersReducedMotion = useReducedMotion()
    const activeInfo =
        benefitInfoMap[activeTabTitle] ?? benefitInfoMap[tabTitles[0] ?? ''] ?? benefitInfoContentMap['Power Rank']

    useLayoutEffect(() => {
        const strip = tabsStripRef.current
        if (!strip) {
            return
        }
        const escaped = typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(activeTabTitle) : activeTabTitle
        const tabEl = strip.querySelector<HTMLElement>(`[data-benefit-tab="${escaped}"]`)
        const behavior = tabStripHasScrolledOnceRef.current ? 'smooth' : 'auto'
        tabStripHasScrolledOnceRef.current = true
        tabEl?.scrollIntoView({ behavior, inline: 'center', block: 'nearest' })
    }, [activeTabTitle])

    useEffect(() => {
        trackNewEliteBottomSheetLoaded(analyticsScreen, NewEliteLandingBottomSheetName.BenefitsInfo, {
            ...buyCtaExtras,
            buttonLabel: initialTabTitle
        })
    }, [analyticsScreen, initialTabTitle])

    const handleTabClick = (tabTitle: string) => {
        if (tabTitle === activeTabTitle) return
        trackNewEliteUserClicked(analyticsScreen, NewEliteLandingEventId.BenefitInfoTab, {
            ...buyCtaExtras,
            buttonLabel: tabTitle
        })
        setSlideDirection(Math.sign(tabTitles.indexOf(tabTitle) - tabTitles.indexOf(activeTabTitle)))
        setActiveTabTitle(tabTitle)
    }

    // Pointer events (not touch) so mouse drags in Safari's responsive mode also swipe
    const handleContentPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
        swipeStartRef.current = { x: event.clientX, y: event.clientY }
    }

    const handleContentPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
        const swipeStart = swipeStartRef.current
        swipeStartRef.current = null
        if (!swipeStart) return
        const deltaX = event.clientX - swipeStart.x
        const deltaY = event.clientY - swipeStart.y
        // Ignore taps and mostly-vertical drags so content scroll and sheet dismiss still work
        if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX || Math.abs(deltaX) <= Math.abs(deltaY)) return
        const nextTabTitle = tabTitles[tabTitles.indexOf(activeTabTitle) + (deltaX < 0 ? 1 : -1)]
        if (nextTabTitle) {
            handleTabClick(nextTabTitle)
        }
    }

    return (
        <BottomSheetContent
            onDismiss={onDismiss}
            bottomSheetClassName='!flex !h-[90vh] !max-h-[90vh] !flex-col'
            bottomSheetHeadClassName='!pt-0'
            bottomSheetBodyClassName='!flex !min-h-0 !flex-1 !flex-col !overflow-hidden !px-0 !pb-0'
        >
            <div className='flex min-h-0 flex-1 flex-col rounded-t-2xl bg-white pt-6'>
                <div className='shrink-0 px-5'>
                    <p className='text-lg font-medium leading-[1.2] text-[#232323]'>{sheetTitle}</p>
                </div>
                <div ref={tabsStripRef} className='mt-4 overflow-x-auto no-scrollbar px-5 border-b border-grey80'>
                    <div className='flex min-w-max items-center gap-6'>
                        {tabTitles.map((tabTitle) => (
                            <div
                                key={tabTitle}
                                data-benefit-tab={tabTitle}
                                onClick={() => handleTabClick(tabTitle)}
                                className='shrink-0 cursor-pointer'
                            >
                                <p className={`text-[15px] leading-[1.4] ${activeTabTitle === tabTitle ? 'font-medium text-[#232323]' : 'font-normal text-grey40'}`}>
                                    {tabTitle}
                                </p>
                                <div className={`mt-2 h-[2px] rounded ${activeTabTitle === tabTitle ? 'bg-[#232323]' : 'bg-transparent'}`} />
                            </div>
                        ))}
                    </div>
                </div>
                <motion.div
                    key={activeTabTitle}
                    initial={prefersReducedMotion ? false : { x: slideDirection * 48, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                    onPointerDown={handleContentPointerDown}
                    onPointerUp={handleContentPointerUp}
                    onPointerCancel={() => {
                        swipeStartRef.current = null
                    }}
                    className='flex min-h-0 flex-1 select-none flex-col overflow-hidden'
                >
                    {activeInfo?.rive ? (
                        <div className='aspect-[520/480] w-full shrink-0 overflow-hidden'>
                            <RiveComponent
                                key={activeTabTitle}
                                src={activeInfo.rive}
                                animations='Timeline 1'
                                fit={RiveFit.FILL}
                                alignment={RiveAlignment.CENTER}
                            />
                        </div>
                    ) : null}
                    <div className='min-h-0 flex-1 overflow-y-auto no-scrollbar px-5 pb-8'>
                        {/* {activeInfo.comingSoon ? (
                            <div
                                role={GROUPED_STATIC_TEXT_ROLE}
                                aria-label='Coming soon: EXCLUSIVE to yearly plan'
                                className='mt-10 mb-4'
                            >
                                <p
                                    aria-hidden='true'
                                    className='border-l-[2px] border-[#CC5C2D] bg-[linear-gradient(90deg,_rgba(204,92,45,0.07)_0%,_rgba(243,243,243,0)_100%)] pl-[4px] text-[12px] uppercase tracking-[0.5px] text-[#CC5C2D]'
                                >
                                    Coming soon: EXCLUSIVE to yearly plan
                                </p>
                            </div>
                        ) : null} */}
                        {activeInfo.eyebrow ? (
                            <p className='mt-10 text-13 font-medium leading-[1.4] text-prime50'>
                                {activeInfo.eyebrow}
                            </p>
                        ) : null}
                        <div
                            role={GROUPED_STATIC_TEXT_ROLE}
                            aria-label={activeInfo.titleAriaLabel}
                            className={`text-xl font-medium leading-[1.3] text-[#232323] ${activeInfo.eyebrow ? 'mt-2' : 'mt-10'
                                }`}
                        >
                            <span aria-hidden='true'>
                                <RenderHTML innerHTMLText={activeInfo.title} />
                            </span>
                        </div>
                        <div
                            role={GROUPED_STATIC_TEXT_ROLE}
                            aria-label={activeInfo.description}
                            className='mt-4 text-sm leading-[1.6] text-grey40'
                        >
                            <span aria-hidden='true'>{activeInfo.description}</span>
                        </div>
                    </div>
                </motion.div>
            </div>
        </BottomSheetContent>
    )
}

export function getBenefitTabTitles(sections: BenefitSection[]): string[] {
    return sections.flatMap((section) => section.items.map((item) => item.title))
}

/** Tab strip order for `BenefitsInfoBottomSheet` (key features + benefits use the same keys as `benefitInfoContentMap`). */
export const eliteBenefitInfoTabTitles = getBenefitTabTitles(benefitSections)

export type BenefitInfoContent = {
    rive: string
    sectionTitle: string
    /** Optional eyebrow above the title (e.g. Elite Prime Exclusive). */
    eyebrow?: string
    title: string
    titleAriaLabel: string
    description: string
    comingSoon?: boolean
}

export const benefitInfoContentMap: Record<string, BenefitInfoContent> = {
    'Power Rank': {
        rive: '/rives/new-elite/Rank.riv',
        sectionTitle: 'FUND SELECTION & REVIEW',
        title: '<span class="font-bold text-black">Power Rank</span> <span class="text-grey40 font-normal">for effortless fund selection</span>',
        titleAriaLabel: 'Power Rank for effortless fund selection',
        description: 'Always invest in top ranked funds, without the guesswork. Know how a fund is performing within its category based on its consistency, volatility, and recent returns in the market.',
    },
    'Power Select': {
        rive: '/rives/new-elite/Select.riv',
        sectionTitle: 'FUND SELECTION & REVIEW',
        title: '<span class="font-bold text-black">Power Select</span> <span class="text-grey40 font-normal">for comprehensive fund comparison</span>',
        titleAriaLabel: 'Power Select for comprehensive fund comparison',
        description: 'Compare funds side-by-side within a category. Access the Elite view with Ranks, Scores, and more to identify funds that best fit your portfolio.',
    },
    'Rank Trends': {
        rive: '/rives/new-elite/Rank_trend.riv',
        sectionTitle: 'FUND SELECTION & REVIEW',
        title: '<span class="font-bold text-black">Rank Trends</span> <span class="text-grey40 font-normal">for clearer performance tracking</span>',
        titleAriaLabel: 'Rank Trends for clearer performance tracking',
        description: 'Track how a fund’s rank has changed over the last 24 months and clearly see trends in its consistency and performance.',
    },
    'Power Rebalance': {
        rive: '/rives/new-elite/Rebalance.riv',
        sectionTitle: 'PORTFOLIO REBALANCING',
        title: '<span class="font-bold text-black">Power Rebalance</span> <span class="text-grey40 font-normal">for guided portfolio rebalancing</span>',
        titleAriaLabel: 'Power Rebalance for guided portfolio rebalancing',
        description: 'Exit underperforming funds and switch to stronger alternatives with step-by-step, tax-efficient rebalancing guidance.',
    },
    'Regular to Direct plan': {
        rive: '/rives/new-elite/R2D.riv',
        sectionTitle: 'PORTFOLIO REBALANCING',
        title: '<span class="font-bold text-black">Regular to Direct Plan</span> <span class="text-grey40 font-normal">for easy savings on high expense ratios</span>',
        titleAriaLabel: 'Regular to Direct Plan for easy savings on high expense ratios',
        description: `Identify the extra costs in your Regular funds and switch to Direct plans with guided, tax-efficient recommendations.`,
    },
    'Elite Lens': {
        rive: '/rives/new-elite/Lens.riv',
        sectionTitle: 'HIGH QUALITY RESEARCH',
        title: '<span class="font-bold text-black">Elite Lens</span> <span class="text-grey40 font-normal">for insights that help you invest with conviction.</span>',
        titleAriaLabel: 'Elite Lens for insights that help you invest with conviction.',
        description: 'Change the way you look at investing. Each week, a new edition delivers one sharp concept - an overlooked truth, a reframed perspective, or a tested principle that helps you see better & decide better.',
    },
    'Tax Saving Plan': {
        rive: '/rives/new-elite/taxharvesting.riv',
        sectionTitle: 'ALLOCATION',
        title: '<span class="font-bold text-black">Tax Saving Plan</span> <span class="text-grey40 font-normal">for simplified tax planning</span>',
        titleAriaLabel: 'Tax Saving Plan for simplified tax planning',
        description: 'Make the most of your yearly tax benefits. Utilise your yearly ₹1.25L LTCG exemption and discover ways to further reduce your tax liability.',

    },
    'Power Allocation': {
        comingSoon: true,
        rive: '/rives/new-elite/Allocation.riv',
        sectionTitle: 'ALLOCATION & TAX',
        title: '<span class="font-bold text-black">Power Allocation</span> <span class="text-grey40 font-normal"> for a portfolio aligned with your financial goals</span>',
        titleAriaLabel: 'Power Allocation for a portfolio aligned with your financial goals',
        description: 'Understand the ideal allocation for your portfolio across fund categories and invest with a strategy tailored to your goals and risk profile.',
    },
    'SIP Strategy': {
        comingSoon: true,
        rive: '/rives/new-elite/SIP_recommendation.riv',
        sectionTitle: 'ALLOCATION & TAX',
        title: '<span class="font-bold text-black">SIP Strategy</span> <span class="text-grey40 font-normal">for investing regularly with a plan</span>',
        titleAriaLabel: 'SIP Strategy for investing regularly with a plan',
        description: `Put your monthly SIPs in the right funds. SIP Strategy, a part of Power Allocation, tells you where to invest and how much — so your money automatically gets invested every month according to your ideal portfolio strategy.`,
    }
}